import { useEffect, useMemo, useState } from 'react';
import { getDocs, onSnapshot, type Query, type QueryConstraint } from '../db/rethink-firestore';
import { col, q, where, type CollectionName } from '../collections';
import type { DocWithId } from './service';
import { reportDataError } from './errorReport';

/**
 * Structural view of a QueryConstraint for cache-key building only. The
 * constraint classes keep their fields private, so keys are derived through
 * this `unknown`-typed view (stringified field-by-field) instead of an `any`
 * cast — no `any` ever leaks into consumers.
 */
interface ConstraintKeyShape {
  readonly field?: unknown;
  readonly op?: unknown;
  readonly value?: unknown;
  readonly direction?: unknown;
  readonly n?: unknown;
  readonly fields?: unknown;
}

function constraintKey(constraints: QueryConstraint[]): string {
  return constraints
    .map((c) => {
      const s = c as unknown as ConstraintKeyShape;
      switch (c.type) {
        case 'where':
          return `w:${String(s.field)}${String(s.op)}${JSON.stringify(s.value)}`;
        case 'orderBy':
          return `o:${String(s.field)}:${String(s.direction)}`;
        case 'limit':
          return `l:${String(s.n)}`;
        case 'select':
          return `s:${Array.isArray(s.fields) ? s.fields.join(',') : ''}`;
        case 'startAt':
        case 'startAfter':
        case 'endAt':
        case 'endBefore':
          return `${c.type}:${JSON.stringify(s.value)}`;
        default:
          return `${c.type}`;
      }
    })
    .join('&');
}

interface LiveState<T> {
  data: DocWithId<T>[];
  loading: boolean;
  error: Error | null;
}

/**
 * Module-level stale-while-revalidate cache for live queries. Consumers of the
 * same collection+constraints share one entry, so navigating away and back
 * within the TTL renders the last-known data instantly instead of re-fetching.
 * Entries are dropped when a query is disabled (null collection or
 * enabled=false) so sign-out / impersonation switches never leak data.
 * Copies are cached, and consumers get copies, so in-place mutations by a
 * screen can never corrupt the shared entry.
 */
const QUERY_CACHE_TTL = 30_000;
const queryCache = new Map<string, { data: DocWithId<unknown>[]; expires: number }>();

/** Live, realtime collection query. Constraints are memoized by value. */
export function useLiveQuery<T = unknown>(
  collectionName: CollectionName | null,
  constraints: QueryConstraint[] = [],
  enabled = true,
): LiveState<T> {
  const [state, setState] = useState<LiveState<T>>(() => {
    const cached = collectionName
      ? queryCache.get(`${collectionName}|${constraintKey(constraints)}`)
      : undefined;
    if (cached && cached.expires > Date.now()) {
      // Seed with last-known data so remounts render instantly; the live
      // subscription below reconciles with fresh data in the background.
      return { data: [...cached.data] as DocWithId<T>[], loading: false, error: null };
    }
    return { data: [], loading: true, error: null };
  });
  const key = useMemo(() => constraintKey(constraints), [constraints]);
  const cacheKey = collectionName ? `${collectionName}|${key}` : null;

  useEffect(() => {
    if (!collectionName || !enabled) {
      // Clear stale results when disabled so previous data never leaks into a
      // new scope (e.g. stopping admin impersonation must drop the impersonated
      // user's query result).
      if (cacheKey) queryCache.delete(cacheKey);
      setState({ data: [], loading: false, error: null });
      return;
    }
    const cachedFresh = cacheKey ? (queryCache.get(cacheKey)?.expires ?? 0) > Date.now() : false;
    if (!cachedFresh) setState((s) => ({ ...s, loading: true, error: null }));
    const queryInstance: Query<T> = q<T>(col<T>(collectionName), ...constraints);

    const unsubscribe = onSnapshot(
      // Boundary cast: doc interfaces lack an index signature so Query<T> is
      // not structurally assignable to Query<DocumentData>; narrowed to the
      // exact adapter type, never `any`.
      queryInstance as unknown as Query,
      (snapshot) => {
        const items = snapshot.docs.map((d) => ({ id: d.id, ...d.data() }) as DocWithId<T>);
        setState({ data: items, loading: false, error: null });
        if (cacheKey) {
          queryCache.set(cacheKey, {
            data: items.slice() as DocWithId<unknown>[],
            expires: Date.now() + QUERY_CACHE_TTL,
          });
        }
      },
      (err) => {
        // Reported (not console-logged): ToastProvider surfaces it to the user.
        reportDataError(collectionName, err);
        setState((s) => ({ ...s, loading: false, error: err }));
      },
    );
    return unsubscribe;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [collectionName, key, enabled]);

  return state;
}

interface LiveDocState<T> {
  data: T | null;
  loading: boolean;
  error: Error | null;
}

/** Live, realtime single document — fetched via a server-scoped id filter. */
export function useLiveDoc<T = unknown>(
  collectionName: CollectionName | null,
  id: string | null,
): LiveDocState<T> {
  const [state, setState] = useState<LiveDocState<T>>({ data: null, loading: true, error: null });

  useEffect(() => {
    if (!collectionName || !id) {
      setState({ data: null, loading: false, error: null });
      return;
    }
    setState((s) => ({ ...s, loading: true, error: null }));

    let disposed = false;
    const colRef = col<T>(collectionName);

    // Scoped real-time snapshot on a single doc via an `id` filter, instead of
    // pulling the whole collection and searching client-side. The defensive
    // client-side id match keeps correctness if the backend ignores the filter.
    const queryInstance = q<T>(colRef, where('id', '==', id));
    const unsubscribe = onSnapshot(
      // Boundary cast, see useLiveQuery above.
      queryInstance as unknown as Query,
      (snapshot) => {
        if (disposed) return;
        const found = snapshot.docs.find((d) => d.id === id) ?? snapshot.docs[0] ?? null;
        setState({ data: (found?.data() as T) ?? null, loading: false, error: null });
      },
      (err) => {
        reportDataError(collectionName, err);
        if (!disposed) setState((s) => ({ ...s, loading: false, error: err }));
      },
    );
    return () => {
      disposed = true;
      unsubscribe();
    };
  }, [collectionName, id]);

  return state;
}

/** Pull-to-simple one-shot fetch (non realtime). */
export function useQueryOnce<T = unknown>(
  collectionName: CollectionName | null,
  constraints: QueryConstraint[] = [],
  enabled = true,
): LiveState<T> {
  const key = useMemo(() => constraintKey(constraints), [constraints]);
  const [state, setState] = useState<LiveState<T>>({ data: [], loading: true, error: null });

  useEffect(() => {
    if (!collectionName || !enabled) {
      setState({ data: [], loading: false, error: null });
      return;
    }
    let cancelled = false;
    setState((s) => ({ ...s, loading: true, error: null }));
    getDocs(q<T>(col<T>(collectionName), ...constraints) as unknown as Query)
      .then((snap) => {
        if (cancelled) return;
        setState({
          data: snap.docs.map((d) => ({ id: d.id, ...d.data() }) as DocWithId<T>),
          loading: false,
          error: null,
        });
      })
      .catch((err) => {
        reportDataError(collectionName, err);
        if (!cancelled) setState((s) => ({ ...s, loading: false, error: err }));
      });
    return () => {
      cancelled = true;
    };
  }, [collectionName, key, enabled]);

  return state;
}
